/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { FiChevronDown } from 'react-icons/fi'
import { NavLink } from 'react-router-dom'

/**
 * One titled group of the left nav (Applications, Dashboards, Actions): a heading whose caret
 * folds the group away. With the sidebar down to icons the heading gives way to a divider and
 * the items always show, since there's no caret to bring them back.
 */
export const NavSection = ({
  id,
  label,
  collapsed,
  onToggle,
  isIconOnly,
  trailing,
  first = false,
  children,
}: {
  id: string
  label: string
  collapsed: boolean
  onToggle: () => void
  isIconOnly: boolean
  /** Controls at the right of the heading, such as the Dashboards edit toggle. */
  trailing?: React.ReactNode
  first?: boolean
  children: React.ReactNode
}) => {
  const bodyId = `nav-section-${id}`
  if (isIconOnly) {
    return (
      <div className={first ? '' : 'mt-2 pt-2 border-t border-base-300'} data-testid={`nav-section-${id}`}>
        {children}
      </div>
    )
  }
  return (
    <div className={first ? '' : 'mt-2'} data-testid={`nav-section-${id}`}>
      <div className="flex justify-between items-center">
        <button
          type="button"
          className="flex-1 flex items-center gap-1 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-left cursor-pointer rounded-lg hover:bg-base-200"
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <FiChevronDown
            className={`w-3.5 h-3.5 shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
            aria-hidden
          />
          {label}
        </button>
        {trailing}
      </div>
      {!collapsed && <div id={bodyId}>{children}</div>}
    </div>
  )
}

/** A nav entry: icon, label and description, highlighted while its route is open. */
export const NavItem = ({
  to,
  end,
  icon,
  label,
  description,
  isIconOnly,
}: {
  to: string
  end?: boolean
  icon: React.ReactNode
  label: string
  description?: string
  isIconOnly: boolean
}) => (
  <NavLink to={to} end={end}>
    {({ isActive }) => (
      <div
        className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? 'bg-primary text-primary-content'
            : 'text-base-content/70 hover:bg-base-300 hover:text-base-content'
        }`}
        title={isIconOnly ? label : undefined}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="shrink-0 w-5 h-5 flex items-center justify-center">{icon}</span>
          {!isIconOnly && (
            <div className="flex-1 min-w-0">
              <div className="truncate">{label}</div>
              {description && <div className="text-xs opacity-75 truncate">{description}</div>}
            </div>
          )}
        </div>
      </div>
    )}
  </NavLink>
)

/**
 * A nav entry that leaves the app, such as Fresh: styled like NavItem but never active, with
 * the new-tab icon trailing so it reads as opening elsewhere.
 */
export const ExternalNavItem = ({
  icon,
  label,
  description,
  isIconOnly,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  description?: string
  isIconOnly: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    className="cursor-pointer w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors text-base-content/70 hover:bg-base-300 hover:text-base-content"
    title={isIconOnly ? `${label} (opens in a new tab)` : undefined}
    aria-label={`${label} (opens in a new tab)`}
  >
    <span className="shrink-0 w-5 h-5 flex items-center justify-center">{icon}</span>
    {!isIconOnly && (
      <div className="flex-1 min-w-0">
        <div className="truncate">{label}</div>
        {description && <div className="text-xs opacity-75 truncate">{description}</div>}
      </div>
    )}
  </button>
)
